// Sistema de Notificaciones — Ziyup
// Wave F: backend-backed. Backend `/api/notifications/*` es la fuente de verdad.
// Caché local `lineage_notifications` se hidrata tras login y se actualiza en
// write-through tras cada acción. Eventos: `lineage_notifications_updated`
// Tipos cubiertos: competitions, tournaments, ranking, social, battles, money,
// courses, shop, sponsorship, booking, live, system

import { notificationsApi } from '@/lib/notificationsApi';
import { tr, localeTag, getCurrentLang } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';
import { describeRent } from '@/components/Shop/RentTermsFields';

const rentLabel = (rent) => describeRent(rent);

const STORAGE_KEY = 'lineage_notifications';
const SETTINGS_KEY = 'lineage_notif_settings';
const SEEDED_KEY_PREFIX = 'lineage_notif_seeded_';

// ============================================================
// Categorías internas + mapping a tabs del filtro UI
// ============================================================
export const CATEGORIES = {
  COMPETITIONS: 'competitions',
  TOURNAMENTS: 'tournaments',
  RANKING: 'ranking',
  SOCIAL: 'social',
  MONEY: 'money',
  COURSES: 'courses',
  SHOP: 'shop',
  SPONSORSHIP: 'sponsorship',
  BOOKING: 'booking',
  LIVE: 'live',
  SYSTEM: 'system'
};

// Agrupación de categorías por tab de filtro (requisito del usuario)
export const FILTER_TABS = [
  { id: 'all', label: tr("Todas"), categories: null },
  { id: 'competitions', label: tr("Retos"), categories: [CATEGORIES.COMPETITIONS, CATEGORIES.LIVE] },
  { id: 'money', label: tr("Dinero"), categories: [CATEGORIES.MONEY, CATEGORIES.COURSES, CATEGORIES.SHOP, CATEGORIES.SPONSORSHIP] },
  { id: 'social', label: tr("Social"), categories: [CATEGORIES.SOCIAL] },
  { id: 'tournaments', label: tr("Torneos"), categories: [CATEGORIES.TOURNAMENTS] },
  { id: 'system', label: tr("Sistema"), categories: [CATEGORIES.SYSTEM, CATEGORIES.RANKING, CATEGORIES.BOOKING] }
];

export const PRIORITIES = { HIGH: 'high', MEDIUM: 'medium', LOW: 'low' };

// ============================================================
// Definición de todos los tipos de notificación
// Mini-patrón bilingüe (aprobado por founder, jun-2026): estas helpers leen
// el idioma activo del mismo almacén que LanguageContext. El resto del
// catálogo sigue en español fijo; extensible por tipo cuando se pida.
const _lang = () => getCurrentLang();
const _fmtDeadline = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(localeTag(), {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// key → { category, priority, icon, title(params), desc(params), action(params) }
// icon es el nombre de lucide-react (string) que se mapea en el componente UI
// action es opcional: { label, path } o null
// ============================================================
// Bloque 3 — avisos de modelos: mismo tipo para torneos y ligas (params.eventType).
const modelEventName = (p) => p.leagueName || p.tournamentName || p.eventName || (p.eventType === 'league' ? tr("Liga") : tr("Torneo"));
const modelEventPath = (p) => (p.eventType === 'league' ? '/leagues' : (p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments'));
// Arreglo 4 (oct-2026) — citas: fecha/hora legible y deep link a la agenda del día exacto.
const _when = (p) => {
  if (p.when) return p.when;
  if (!p.date) return '';
  const [y, m, d] = String(p.date).split('-').map(Number);
  const dt = y && m && d ? new Date(y, m - 1, d) : null;
  const day = dt ? dt.toLocaleDateString(localeTag(), { weekday: 'short', day: 'numeric', month: 'short' }) : p.date;
  return `${day}${p.start_time ? ` · ${p.start_time}` : ''}`;
};
const _agendaPath = (p) => `/profile?tab=agenda${p.date ? `&date=${p.date}` : ''}${p.booking_id ? `&booking=${p.booking_id}` : ''}`;


export const NOTIFICATION_TYPES = {
  // --- Competitions ---
  // Iter 206.1 — Premio de Ziyup Credits (retos comunitarios) → /credits.
  credits_awarded:      { category: CATEGORIES.COMPETITIONS, priority: PRIORITIES.HIGH, icon: 'Coins',
    title: (p) => tr("¡Ganaste {0} Ziyup Credits!", String(p.credits ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')),
    desc:  (p) => p.challengeName
      ? tr("Premio del reto \"{0}\"{1}", p.challengeName, p.placement ? ` · Puesto #${p.placement}` : '')
      : tr("Premio de reto — úsalos para impulsar tu perfil"),
    action:() => ({ label: tr("Ver mis Credits"), path: '/credits' }) },
  challenge_prize_awarded: { category: CATEGORIES.COMPETITIONS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => tr("¡Ganaste {0} {1}!", String(p.amount ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, '.'), p.unit || tr("TT")),
    desc:  (p) => p.challengeName
      ? tr("Premio del reto \"{0}\"{1}", p.challengeName, p.placement ? ` · Puesto #${p.placement}` : '')
      : tr("Premio de reto comunitario"),
    action:(p) => ({ label: tr("Ver reto"), path: p.challengeId ? `/reto/${p.challengeId}` : '/feed' }) },

  // --- Marketplace M3 (jun-2026) — moderación de cursos ---
  marketplace_course_review: { category: CATEGORIES.SHOP, priority: PRIORITIES.HIGH, icon: 'GraduationCap',
    title: (p) => p.status === 'active'
      ? tr("Curso aprobado")
      : tr("Curso rechazado"),
    desc:  (p) => p.status === 'active'
      ? tr("\"{0}\" ya está activo", p.courseTitle || '')
      : (p.reason ? `"${p.courseTitle || ''}": ${p.reason}` : tr("\"{0}\" no pasó la revisión", p.courseTitle || '')),
    action:() => ({ label: tr("Mis cursos"), path: '/seller/courses' }) },

  // --- Marketplace M2 (jun-2026) — moderación de productos ---
  marketplace_product_review: { category: CATEGORIES.SHOP, priority: PRIORITIES.HIGH, icon: 'Package',
    title: (p) => p.status === 'active'
      ? tr("Producto aprobado")
      : tr("Producto rechazado"),
    desc:  (p) => p.status === 'active'
      ? tr("\"{0}\" ya está activo en tu catálogo", p.productTitle || '')
      : (p.reason ? `"${p.productTitle || ''}": ${p.reason}` : tr("\"{0}\" no pasó la revisión", p.productTitle || '')),
    action:() => ({ label: tr("Mis productos"), path: '/seller/products' }) },

  // --- Marketplace M1 (jun-2026) — estado de Vendedor Verificado ---
  marketplace_seller_status: { category: CATEGORIES.SHOP, priority: PRIORITIES.HIGH, icon: 'Store',
    title: (p) => ({
      active: tr("¡Eres Vendedor Verificado!"),
      rejected: tr("Solicitud de vendedor rechazada"),
      suspended: tr("Cuenta de vendedor suspendida"),
    }[p.status] || tr("Actualización de tu solicitud de vendedor")),
    desc:  (p) => p.status === 'active'
      ? tr("Tu tienda \"{0}\" ya puede publicar en Ziyup", p.storeName || '')
      : (p.reason || tr("Tienda \"{0}\"", p.storeName || '')),
    action:() => ({ label: tr("Ver mi estado"), path: '/seller/verification' }) },

  // --- Tournaments ---
  tournament_new:       { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => tr("Nuevo torneo: {0}", p.tournamentName),
    desc:  (p) => tr("Nivel {0} · Premio ${1}", p.level, p.prize || '—'),
    action:(p) => ({ label: tr("Ver torneo"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_registered:{ category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Ticket',
    title: () => tr("Inscripción confirmada"),
    desc:  (p) => `${p.tournamentName} · Empieza ${p.startsOn || tr("próximamente")}`,
    action:(p) => ({ label: tr("Ver bracket"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_reminder:  { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.LOW, icon: 'BellRing',
    title: (p) => tr("Recordatorio: {0}", p.tournamentName),
    desc:  (p) => tr("Empieza {0} — revisa tu entrada y llega con tiempo", p.when || tr("pronto")),
    action:(p) => ({ label: tr("Ver torneo"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_match_ready:{category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Swords',
    title: () => tr("Próxima batalla"),
    desc:  (p) => tr("{0} · Ronda {1}{2}", p.tournamentName, p.round, p.opponentName ? ` vs ${p.opponentName}` : ''),
    action:(p) => ({ label: tr("Ver bracket"), path: p.tournamentId ? `/tournament/${p.tournamentId}?tab=bracket` : '/tournaments' }) },
  tournament_round_result:{category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Award',
    title: (p) => p.advanced ? tr("¡Avanzaste de ronda!") : tr("Eliminado del torneo"),
    desc:  (p) => tr("{0} · Ronda {1}", p.tournamentName, p.round),
    action:(p) => ({ label: tr("Ver bracket"), path: p.tournamentId ? `/tournament/${p.tournamentId}?tab=bracket` : '/tournaments' }) },
  tournament_finished:  { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'Crown',
    title: (p) => tr("Torneo finalizado: {0}", p.tournamentName),
    desc:  (p) => tr("Campeón: {0}", p.championName),
    action:(p) => ({ label: tr("Ver resultados"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  // --- Fase A (sep-2026): pool de modelos por torneo · Bloque 3: también ligas (eventType=league) ---
  model_application_new: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.LOW, icon: 'UserPlus',
    title: (p) => tr("Nuevo modelo postulado{0}", p.city ? ` · ${p.city}` : ''),
    desc:  (p) => tr("{0}{1} · Revisa el pool y elige a tu modelo", modelEventName(p), p.commitment ? ` · ${p.commitment === 'season' ? tr("toda la temporada") : tr("un corte")}` : ''),
    action:(p) => ({ label: tr("Ver pool"), path: modelEventPath(p) }) },
  model_selected:        { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Sparkles',
    title: (p) => tr("¡{0} te eligió como modelo!", p.barberName || tr("Un barbero")),
    desc:  (p) => `${modelEventName(p)}${p.benefit ? ` · ${p.benefit}` : ''}`,
    action:(p) => ({ label: tr("Ver detalles"), path: '/applications' }) },
  model_released:        { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'UserMinus',
    title: (p) => tr("{0} liberó tu postulación", p.barberName || tr("El barbero")),
    desc:  (p) => tr("{0} · Vuelves al pool: otro barbero puede elegirte", modelEventName(p)),
    action:(p) => ({ label: p.eventType === 'league' ? tr("Ver liga") : tr("Ver torneo"), path: p.eventType === 'league' ? '/leagues' : (p.tournamentId ? `/tournament/${p.tournamentId}` : '/applications') }) },
  model_cancelled:       { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'UserMinus',
    title: (p) => `${p.modelName || tr("Tu modelo")} ${p.eventType === 'league' ? tr("se retiró") : tr("canceló su postulación")}`,
    desc:  (p) => tr("{0} · {1}Elige otro modelo del pool", modelEventName(p), p.eventType === 'league' && p.cutsDone > 0 ? `Sus ${p.cutsDone} corte(s) enviados se conservan · ` : ''),
    action:(p) => ({ label: tr("Ver pool"), path: modelEventPath(p) }) },
  model_cut_done:        { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.LOW, icon: 'CheckCircle2',
    title: () => tr("¡Tu corte fue enviado a evaluación!"),
    desc:  (p) => tr("{0}{1} · Gracias por ser modelo. Ya puedes postularte de nuevo.", modelEventName(p), p.cutSeq ? ` · Corte #${p.cutSeq}` : ''),
    action:() => ({ label: tr("Ver mis postulaciones"), path: '/applications' }) },
  // --- Iter 207 F4: actionable in-person tournament notifications ---
  tournament_payment_confirmed: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'CheckCircle2',
    title: () => tr("Pago confirmado"),
    desc:  (p) => tr("{0}{1} · Ya estás en el torneo", p.tournamentName, p.amount ? ` · $${p.amount} USD` : ''),
    action:(p) => ({ label: tr("Ver mi entrada"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=entry` : '/tournaments' }) },
  tournament_entry_ready: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Ticket',
    title: () => tr("Tu entrada está lista"),
    desc:  (p) => `${p.tournamentName}${p.checkinCode ? tr("· Código LBW-{0}", p.checkinCode) : ''}`,
    action:(p) => ({ label: tr("Abrir QR"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=entry` : '/tournaments' }) },
  tournament_checkin_open: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'DoorOpen',
    title: () => tr("Check-in abierto"),
    desc:  (p) => tr("{0} · Presenta tu QR en la entrada{1}", p.tournamentName, p.closesAt ? ` · Cierra ${p.closesAt}` : ''),
    action:(p) => ({ label: tr("Mi entrada"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=entry` : '/tournaments' }) },
  live_photos_late_review: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'Camera',
    title: () => tr("Fotos tardías por revisar"), desc: (p) => tr("{0}", p.tournamentName), action: (p) => ({ label: tr("Revisar"), path: '/admin/live-rubric' }) },
  live_photos_judging_open: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Camera',
    title: () => tr("Ya puedes evaluar fotos"), desc: (p) => tr("{0} · plazo hasta {1}", p.tournamentName, p.deadline_at ? new Date(p.deadline_at).toLocaleString() : ''), action: (p) => ({ label: tr("Evaluar"), path: p.tournamentId ? `/live/${p.tournamentId}/judge-photos` : '/tournaments' }) },
  live_results_published: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => (p.rank ? tr("Resultados: quedaste #{0}", p.rank) : tr("Resultados publicados")), desc: (p) => tr("{0}", p.tournamentName), action: (p) => ({ label: tr("Ver resultados"), path: p.tournamentId ? `/live/${p.tournamentId}/results` : '/tournaments' }) },
  judge_program_approved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel', title: () => tr("¡Eres juez de Ziyup!"), desc: (p) => p.note || tr("Tu postulación fue aprobada"), action: () => ({ label: tr("Ver"), path: '/judge/onboarding' }) },
  judge_program_rejected: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel', title: () => tr("Postulación de juez no aprobada"), desc: (p) => p.note || '', action: () => ({ label: tr("Ver"), path: '/judge/onboarding' }) },
  judge_program_needs_info: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel', title: () => tr("Tu postulación necesita más información"), desc: (p) => p.note || '', action: () => ({ label: tr("Completar"), path: '/judge/onboarding' }) },
  judge_suspended_shift: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'AlertTriangle', title: () => tr("Turno suspendido temporalmente"), desc: (p) => tr("{0} · un juez de reserva tomó tu grupo; tus calificaciones quedan en revisión", p.tournamentName), action: () => ({ label: tr("Ver"), path: '/judge' }) },
  judge_reserve_called: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Video', title: () => tr("Te necesitamos como juez de reserva"), desc: (p) => p.tournamentName, action: (p) => ({ label: tr("Entrar"), path: `/live/${p.tournamentId}/judge-live` }) },
  judge_sanction: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel', title: (p) => tr("Sanción como juez: {0}", p.kind), desc: (p) => p.note, action: () => ({ label: tr("Ver y apelar"), path: '/judge/onboarding' }) },
  live_phase_advanced: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => tr("¡Clasificaste a {0}!", p.phase_name || tr("la siguiente fase")), desc: (p) => tr("{0} · {1}{2}", p.tournamentName, p.starts_at ? new Date(p.starts_at).toLocaleString() : '', p.group ? ` · ${p.group}` : ''), action: (p) => ({ label: tr("Ver mi tanda"), path: p.path || `/live/${p.tournamentId}/session` }) },
  live_phase_eliminated: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => tr("Tu resultado en {0}: #{1}", p.phase_name || tr("esta fase"), p.rank ?? '—'), desc: (p) => tr("{0} · tu análisis completo está listo", p.tournamentName), action: (p) => ({ label: tr("Ver mi análisis"), path: p.path || `/live/${p.tournamentId}/results` }) },
  live_champion: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: () => tr("¡Eres el campeón!"), desc: (p) => tr("{0}", p.tournamentName), action: (p) => ({ label: tr("Ver resultados"), path: p.path || `/live/${p.tournamentId}/results` }) },
  live_session_assigned: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Clock',
    title: () => tr("Te asignamos tu tanda"), desc: (p) => tr("{0} · {1}", p.tournamentName, p.starts_at ? new Date(p.starts_at).toLocaleString() : ''), action: (p) => ({ label: tr("Ver mi tanda"), path: p.path || `/live/${p.tournamentId}/session` }) },
  live_appeal_resolved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Scale',
    title: (p) => (p.action === 'uphold' ? tr("Tu apelación fue aceptada") : tr("Tu apelación fue desestimada")), desc: (p) => tr("{0}", p.tournamentName), action: (p) => ({ label: tr("Ver resultados"), path: p.tournamentId ? `/live/${p.tournamentId}/results` : '/tournaments' }) },
  live_judge_invite: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'UserPlus',
    title: (p) => tr("Te invitan como juez {0}", p.role === 'photos' ? tr("de fotos") : tr("en vivo")), desc: (p) => tr("{0}", p.tournamentName), action: () => ({ label: tr("Postularme"), path: '/judge' }) },
  live_session_reminder: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Clock',
    title: (p) => (p.when === '15m' ? tr("Tu tanda empieza en 15 minutos") : tr("Tu tanda es mañana")),
    desc:  (p) => tr("{0} · {1}", p.tournamentName, p.starts_at ? new Date(p.starts_at).toLocaleString() : ''),
    action:(p) => ({ label: tr("Ver tanda"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=live` : '/tournaments' }) },
  tournament_checkin_done: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.LOW, icon: 'CheckCircle2',
    title: () => tr("Check-in completado"),
    desc:  (p) => tr("{0} · Ya estás dentro del evento", p.tournamentName),
    action:(p) => ({ label: tr("Mi entrada"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=entry` : '/tournaments' }) },
  tournament_opponent_revealed: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'Eye',
    title: () => tr("Rival revelado"),
    desc:  (p) => p.opponentName
      ? tr("{0} · Te enfrentas a {1}", p.tournamentName, p.opponentName)
      : tr("{0} · Tu bracket ya es visible", p.tournamentName),
    action:(p) => ({ label: tr("Ver rival"), path: p.tournamentId ? `/tournament/${p.tournamentId}?tab=bracket` : '/tournaments' }) },
  tournament_updated: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'Info',
    title: () => tr("Actualización del torneo"),
    desc:  (p) => tr("{0} · Cambiaron detalles del evento, revísalos", p.tournamentName),
    action:(p) => ({ label: tr("Ver torneo"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_schedule_changed: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'CalendarClock',
    title: () => tr("Cambio de horario"),
    desc:  (p) => `${p.tournamentName}${p.newDate ? tr("· Nueva fecha: {0}", p.newDate) : tr("· Revisa el nuevo horario")}`,
    action:(p) => ({ label: tr("Ver horario"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_modality_changed: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'MapPin',
    title: () => tr("Cambio de modalidad"),
    desc:  (p) => tr("{0} · {1} será {2}", p.tournamentName, p.phaseLabel || tr("Una ronda"), p.modality === 'in_person' ? tr("EN PERSONA") : tr("ONLINE")),
    action:(p) => ({ label: tr("Ver torneo"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_prize_pending: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'Hourglass',
    title: () => tr("Premio pendiente de validación"),
    desc:  (p) => tr("{0}{1} · En validación de resultados e identidad", p.tournamentName, p.amount ? ` · $${p.amount} USD` : ''),
    action:(p) => ({ label: tr("Ver premio"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=prize` : '/tournaments' }) },
  tournament_prize_approved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'BadgeCheck',
    title: () => tr("Premio aprobado"),
    desc:  (p) => tr("{0}{1} · El pago está en proceso", p.tournamentName, p.amount ? ` · $${p.amount} USD` : ''),
    action:(p) => ({ label: tr("Estado del premio"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=prize` : '/tournaments' }) },
  tournament_prize_paid: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Banknote',
    title: () => tr("Premio pagado"),
    desc:  (p) => `${p.tournamentName}${p.amount ? ` · $${p.amount} USD` : ''}${p.reference ? ` · Ref: ${p.reference}` : ''}`,
    action:(p) => ({ label: tr("Ver comprobante"), path: p.tournamentId ? `/tournament/${p.tournamentId}?focus=prize` : '/tournaments' }) },
  tournament_cancelled: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'XCircle',
    title: () => tr("Torneo cancelado"),
    desc:  (p) => tr("{0} fue cancelado{1}", p.tournamentName, p.refunded ? ' · Tu inscripción será reembolsada' : ''),
    action:(p) => ({ label: tr("Ver detalles"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },
  tournament_judge_application_approved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel',
    title: () => tr("Postulación de juez aprobada"),
    desc:  (p) => tr("{0} · Ya estás en el roster de jueces", p.tournamentName),
    action:() => ({ label: tr("Panel de Juez"), path: '/judge' }) },
  tournament_judge_application_rejected: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'XCircle',
    title: () => tr("Postulación de juez rechazada"),
    desc:  (p) => `${p.tournamentName}${p.reason ? ` · Motivo: ${p.reason}` : ''}`,
    action:() => ({ label: tr("Torneos disponibles"), path: '/judge' }) },
  // SB3.2a (ago-2026) — selección automática + reserva
  tournament_judge_selected: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel',
    title: (p) => p.promoted ? tr("Promovido: eres juez TITULAR") : tr("Seleccionado: eres juez TITULAR"),
    desc:  (p) => tr("{0} · Quedaste en el panel oficial de jueces", p.tournamentName),
    action:() => ({ label: tr("Panel de Juez"), path: '/judge' }) },
  tournament_judge_reserve: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'Hourglass',
    title: () => tr("Quedaste como juez de RESERVA"),
    desc:  (p) => tr("{0} · Si un titular falla, puedes ser promovido", p.tournamentName),
    action:() => ({ label: tr("Panel de Juez"), path: '/judge' }) },
  tournament_judge_not_selected: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'XCircle',
    title: () => tr("No quedaste seleccionado como juez"),
    desc:  (p) => tr("{0} · Gracias por postularte — habrá más torneos", p.tournamentName),
    action:() => ({ label: tr("Torneos disponibles"), path: '/judge' }) },
  // SB3.2b (ago-2026) — alertas de déficit de jueces al founder
  // L3 Ligas (jun-2026) — jueces de liga + corte evaluado
  league_judge_approved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Gavel',
    title: () => tr("Eres juez de Liga"),
    desc:  (p) => tr("{0} · Quedaste en el panel de jueces de la liga", p.leagueName),
    action:() => ({ label: tr("Panel de Juez"), path: '/judge' }) },
  league_judge_rejected: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'XCircle',
    title: () => tr("Postulación de juez de Liga rechazada"),
    desc:  (p) => `${p.leagueName}${p.reason ? tr("· Motivo: {0}", p.reason) : tr("· Gracias por postularte")}`,
    action:() => ({ label: tr("Ligas disponibles"), path: '/judge' }) },
  league_cut_scored: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Scissors',
    title: (p) => tr("Tu corte fue evaluado: {0} puntos", p.score),
    desc:  (p) => tr("{0} · El puntaje ya compite en tus cruces", p.leagueName),
    action:() => ({ label: tr("Mi Liga"), path: '/leagues' }) },
  // F2 Ligas por fases (jun-2026) — recordatorios de ventana
  league_phase_open: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'CalendarRange',
    title: (p) => tr("Se abrió la ventana de tu {0}", p.cutLabel || tr("corte")),
    desc:  (p) => tr("{0} · {1} abierta — sube tu corte antes del {2}", p.leagueName, p.phaseName, String(p.endsAt || '').slice(0, 10)),
    action:() => ({ label: tr("Subir mi corte"), path: '/leagues' }) },
  league_phase_closing: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'AlarmClock',
    title: (p) => tr("{0} cierra pronto", p.phaseName),
    desc:  (p) => tr("{0} · {1} — si no subes tu {2}, pierdes la fase para siempre", p.leagueName, Number(p.daysLeft) <= 0 ? tr("Cierra HOY") : (Number(p.daysLeft) === 1 ? tr("Queda 1 día") : tr("Quedan {0} días", p.daysLeft)), p.cutLabel || tr("corte")),
    action:() => ({ label: tr("Subir mi corte"), path: '/leagues' }) },
  // F5-B Ligas por fases (jun-2026) — desempate top 50 (corte extra 7 días)
  league_tiebreak_open: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Swords',
    title: (p) => tr("⚔️ Desempate por el puesto #{0}", p.position),
    desc:  (p) => tr("{0} · Empataste con {1} barbero(s) — sube tu corte de desempate antes del {2}", p.leagueName, Math.max(0, Number(p.membersCount || 2) - 1), String(p.endsAt || '').slice(0, 10)),
    action:() => ({ label: tr("Mi Liga"), path: '/leagues' }) },
  league_tiebreak_closing: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'AlarmClock',
    title: (p) => tr("Tu desempate cierra pronto"),
    desc:  (p) => tr("{0} · {1} — sin tu corte de desempate quedas detrás de quienes sí presentaron", p.leagueName, Number(p.daysLeft) <= 0 ? tr("Cierra HOY") : (Number(p.daysLeft) === 1 ? tr("Queda 1 día") : tr("Quedan {0} días", p.daysLeft))),
    action:() => ({ label: tr("Subir mi corte"), path: '/leagues' }) },
  league_tiebreak_scored: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Swords',
    title: (p) => tr("Tu corte de desempate fue evaluado: {0} puntos", p.score),
    desc:  (p) => tr("{0} · El puntaje solo ordena tu grupo de desempate — no suma a tu acumulado", p.leagueName),
    action:() => ({ label: tr("Mi Liga"), path: '/leagues' }) },
  league_tiebreak_resolved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Swords',
    title: (p) => tr("Desempate resuelto — quedaste #{0}", p.finalPosition),
    desc:  (p) => `${p.leagueName} · ${p.submitted ? tr("Tu corte de desempate ({0} pts) definió tu posición final", p.tiebreakScore) : tr("Sin corte de desempate quedaste detrás de quienes sí presentaron")}`,
    action:(p) => ({ label: tr("Ver escalera"), path: p.leagueId ? `/leagues/${p.leagueId}/escalera` : '/leagues' }) },
  // L4-C (jun-2026) — campeón de liga + premio TT
  league_champion: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => (p.coChampions ? tr("🏆 Co-campeón de Liga") : tr("🏆 ¡Ganaste la Liga!")),
    desc:  (p) => {
      const tt = Number(p.amountTt || 0);
      const base = p.coChampions
        ? tr("Compartiste el título de la liga {0}", p.leagueName)
        : tr("Ganaste la liga {0}", p.leagueName);
      return tt > 0 ? tr("{0} — se te acreditaron {1} TT", base, tt.toLocaleString(localeTag())) : base;
    },
    action:() => ({ label: tr("Ver Ligas"), path: '/leagues' }) },
  // F5-C (ago-2026) — premio TT multiposición de liga (posiciones 2..N)
  league_prize_awarded: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Medal',
    title: (p) => tr("🏅 Premio de Liga — quedaste #{0}", p.position),
    desc:  (p) => {
      const tt = Number(p.amountTt || 0);
      return tr("{0} · Terminaste en la posición #{1}", p.leagueName, p.position)
        + (tt > 0 ? tr(" — se te acreditaron {0} TT", tt.toLocaleString(localeTag())) : '');
    },
    action:(p) => ({ label: tr("Ver tabla de premiados"), path: p.leagueId ? `/leagues/${p.leagueId}/escalera` : '/leagues' }) },
  // Ventana del corte igualador (L4-B) — bilingüe
  league_equalizer_window: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'Hourglass',
    title: () => (_lang() === 'en' ? tr("⏳ Equalizer cut enabled") : tr("⏳ Corte igualador habilitado")),
    desc:  (p) => (_lang() === 'en'
      ? `You're tied in ${p.leagueName}. You have until ${_fmtDeadline(p.deadlineAt)} to upload your tiebreak cut from "My League".`
      : tr("Estás empatado en {0}. Tienes hasta el {1} para subir tu corte de desempate desde \"Mi Liga\".", p.leagueName, _fmtDeadline(p.deadlineAt))),
    action:() => ({ label: _lang() === 'en' ? tr("My League") : tr("Mi Liga"), path: '/leagues' }) },
  tournament_judge_deficit: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.HIGH, icon: 'AlertTriangle',
    title: (p) => tr("Faltan {0} {1} en un torneo", p.missing, Number(p.missing) === 1 ? tr("juez") : tr("jueces")),
    desc:  (p) => tr("{0} · Pool de postulantes agotado — la búsqueda sigue abierta", p.tournamentName),
    action:(p) => ({ label: tr("Abrir postulación"), path: p.tournamentId ? `/tournament/${p.tournamentId}/ops?tab=judges` : '/admin' }) },
  tournament_judge_deficit_resolved: { category: CATEGORIES.TOURNAMENTS, priority: PRIORITIES.MEDIUM, icon: 'CheckCircle2',
    title: () => tr("Déficit de jueces resuelto"),
    desc:  (p) => tr("{0} · Titulares y reserva completos de nuevo", p.tournamentName),
    action:(p) => ({ label: tr("Ver postulación"), path: p.tournamentId ? `/tournament/${p.tournamentId}/ops?tab=judges` : '/admin' }) },

  // --- Ranking ---
  rank_up:              { category: CATEGORIES.RANKING, priority: PRIORITIES.MEDIUM, icon: 'TrendingUp',
    title: () => tr("Subiste en el ranking"),
    desc:  (p) => `${p.scope}: #${p.oldRank} → #${p.newRank}`,
    action:() => ({ label: tr("Ver ranking"), path: '/rankings' }) },
  rank_down:            { category: CATEGORIES.RANKING, priority: PRIORITIES.LOW, icon: 'TrendingDown',
    title: () => tr("Bajaste en el ranking"),
    desc:  (p) => `${p.scope}: #${p.oldRank} → #${p.newRank}`,
    action:() => ({ label: tr("Ver ranking"), path: '/rankings' }) },
  entered_top_100:      { category: CATEGORIES.RANKING, priority: PRIORITIES.HIGH, icon: 'Star',
    title: () => tr("¡Entraste al Top 100!"),
    desc:  () => tr("Ya puedes crear cursos en Academy"),
    action:() => ({ label: tr("Crear curso"), path: '/profile' }) },
  entered_top_25:       { category: CATEGORIES.RANKING, priority: PRIORITIES.HIGH, icon: 'Medal',
    title: () => tr("¡Entraste al Top 25!"),
    desc:  () => tr("Ya puedes vender productos en el Marketplace"),
    action:() => ({ label: tr("Añadir producto"), path: '/profile' }) },
  weekly_ranking_update:{ category: CATEGORIES.RANKING, priority: PRIORITIES.LOW, icon: 'BarChart3',
    title: () => tr("Ranking semanal actualizado"),
    desc:  (p) => tr("Estás en #{0} global ({1}{2} esta semana)", p.newRank, p.delta >= 0 ? '+' : '', p.delta),
    action:() => ({ label: tr("Ver ranking"), path: '/rankings' }) },

  // --- Social ---
  social_follow:        { category: CATEGORIES.SOCIAL, priority: PRIORITIES.MEDIUM, icon: 'UserPlus',
    title: (p) => tr("{0} te siguió", p.fromName),
    desc:  () => '',
    action:(p) => ({ label: tr("Ver perfil"), path: p.fromBarberUsername ? `/barber/${p.fromBarberUsername}` : '/profile' }) },
  social_like:          { category: CATEGORIES.SOCIAL, priority: PRIORITIES.LOW, icon: 'Heart',
    title: (p) => tr("{0} le dio like a tu post", p.fromName),
    desc:  () => '',
    action:(p) => ({ label: tr("Ver post"), path: p.postId ? `/post/${p.postId}` : '/feed' }) },
  social_comment:       { category: CATEGORIES.SOCIAL, priority: PRIORITIES.MEDIUM, icon: 'MessageCircle',
    title: (p) => tr("{0} comentó tu post", p.fromName),
    desc:  (p) => p.preview ? `"${p.preview}"` : tr("Nuevo comentario en tu post"),
    action:(p) => ({ label: tr("Ver comentario"), path: p.postId ? `/post/${p.postId}` : '/feed' }) },
  social_reply:         { category: CATEGORIES.SOCIAL, priority: PRIORITIES.MEDIUM, icon: 'CornerUpLeft',
    title: (p) => tr("{0} te respondió", p.fromName),
    desc:  (p) => p.preview ? `"${p.preview}"` : tr("Respuesta a tu comentario"),
    action:(p) => ({ label: tr("Ver hilo"), path: p.postId ? `/post/${p.postId}` : '/feed' }) },
  social_share:         { category: CATEGORIES.SOCIAL, priority: PRIORITIES.LOW, icon: 'Share2',
    title: (p) => tr("{0} compartió tu post", p.fromName),
    desc:  () => '',
    action:() => ({ label: tr("Ver post"), path: '/feed' }) },
  // Iter 205 — Regalo recibido (presentación premium por nivel). El valor
  // LT y la Ganancia generada vienen del BACKEND (creator_share_cents) —
  // nunca se calcula el split en frontend. La ganancia es PRIVADA del
  // receptor (esta notificación solo la ve él).
  gift_received:        { category: CATEGORIES.SOCIAL, priority: PRIORITIES.HIGH, icon: 'Gift',
    title: (p) => p.message || tr("Te enviaron {0}", p.gift_name || tr("un Regalo")),
    desc:  (p) => {
      const parts = [];
      if (p.gift_name && p.price_tokens != null) {
        const crown = p.visual_tier === 'legendary' ? '👑 ' : '';
        parts.push(`${crown}${p.gift_name} — ${String(p.price_tokens).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} LT`);
      }
      if (p.creator_share_cents != null) {
        parts.push(tr("Ganancia generada: ${0}", (Number(p.creator_share_cents) / 100).toFixed(2)));
      }
      return parts.join(' · ');
    },
    action:(p) => ({ label: tr("Ver contenido"), path: p.deep_link || '/feed' }) },
  comment_reaction:     { category: CATEGORIES.SOCIAL, priority: PRIORITIES.LOW, icon: 'ThumbsUp',
    title: (p) => tr("{0} reaccionó a tu comentario", p.fromName),
    desc:  (p) => p.kind === 'dislike' ? tr("Te dio 👎") : tr("Te dio 👍"),
    action:(p) => ({ label: tr("Ver post"), path: p.postId ? `/post/${p.postId}` : '/feed' }) },
  // Iter 163 · Messaging MVP — DM received.
  // Backend emits one notif per message (grouped per conversation via
  // group_key=conv:{id}). Consecutive messages from the same peer collapse
  // in the bell UI thanks to the standard groupNotifications() rules.
  message_received:     { category: CATEGORIES.SOCIAL, priority: PRIORITIES.MEDIUM, icon: 'MessageSquare',
    title: (p) => p.fromName ? tr("Nuevo mensaje de {0}", p.fromName) : tr("Nuevo mensaje"),
    desc:  (p) => p.preview ? `"${p.preview}"` : tr("Toca para responder"),
    action:(p) => ({ label: tr("Abrir chat"), path: p.conversation_id ? `/messages/${p.conversation_id}` : '/messages' }) },
  // Sprint 6 — tipo emitido por el motor de drops; sin él la fila quedaba vacía.
  drop_caught:          { category: CATEGORIES.COMPETITIONS, priority: PRIORITIES.HIGH, icon: 'Gift',
    title: (p) => p.message || tr("¡Atrapaste un Drop!"),
    desc:  () => '',
    action:() => null },

  // --- Battles ---
  // --- Money ---
  tip_received:         { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'DollarSign',
    title: (p) => tr("Propina recibida: ${0}", p.amount),
    desc:  (p) => tr("De {0}{1}", p.fromName, p.message ? ' — "' + p.message + '"' : ''),
    action:() => ({ label: tr("Ver cartera"), path: '/professional' }) },
  course_sale:          { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'GraduationCap',
    title: (p) => tr("Venta de curso: {0}", p.courseName),
    desc:  (p) => tr("${0} (tu parte: ${1}) · {2}", p.amount, p.net, p.buyerName),
    action:() => ({ label: tr("Ver ventas"), path: '/profile' }) },
  product_sale:         { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Package',
    title: (p) => tr("Venta de producto: {0}", p.productName),
    desc:  (p) => tr("${0} (tu parte: ${1}) · {2}", p.amount, p.net, p.buyerName),
    action:() => ({ label: tr("Ver órdenes"), path: '/profile' }) },
  payment_processed:    { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'CheckCircle2',
    title: (p) => tr("Pago procesado: ${0}", p.amount),
    desc:  (p) => p.concept || tr("Transferencia completada"),
    action:() => ({ label: tr("Ver detalles"), path: '/profile' }) },
  withdrawal_available: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Wallet',
    title: (p) => tr("Retiro disponible: ${0}", p.amount),
    desc:  () => tr("Puedes retirar tus ganancias acumuladas"),
    action:() => ({ label: tr("Retirar"), path: '/profile' }) },
  // --- Retiros SB64 (Express / al banco) ---
  withdrawal_paid:      { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Banknote',
    title: (p) => tr("Retiro pagado: ${0}", Number(p.amount_usd || 0).toFixed(2)),
    desc:  (p) => tr("{0}{1} · El dinero ya está en tu cuenta", p.method === 'express' ? tr("Retiro Express") : tr("Retiro al banco"), p.destination_label ? ` · ${p.destination_label}` : ''),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  withdrawal_failed:    { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'XCircle',
    title: (p) => tr("Tu retiro de ${0} no se completó", Number(p.amount_usd || 0).toFixed(2)),
    desc:  (p) => p.reason || (p.method === 'express'
      ? tr("Stripe rechazó el retiro Express. {0}", p.fee_refunded === false ? tr("Estamos devolviendo la comisión.") : tr("La comisión fue devuelta."))
      : tr("Stripe rechazó el retiro al banco. No se movió dinero.")),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  // --- Carril 6 — cashout de regalos / TT ---
  cashout_approved:     { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Banknote',
    title: (p) => tr("Retiro aprobado: ${0}", Number(p.amount_usd || 0).toFixed(2)),
    desc:  (p) => tr("{0} · se transfiere a tu cuenta Stripe en el próximo ciclo", p.source_label || tr("Retiro")),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  cashout_rejected:     { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'XCircle',
    title: (p) => tr("Retiro rechazado: ${0}", Number(p.amount_usd || 0).toFixed(2)),
    desc:  (p) => p.reason || tr("{0} · tu saldo sigue disponible", p.source_label || tr("Retiro")),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  cashout_paid:         { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Banknote',
    title: (p) => tr("${0} ya están en tu cuenta Stripe", Number(p.amount_usd || 0).toFixed(2)),
    desc:  (p) => tr("{0} transferido · retíralo Express o al banco desde Mi Dinero", p.source_label || tr("Retiro")),
    action:() => ({ label: tr("Retirar"), path: '/money' }) },
  refund_request:       { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Undo2',
    title: () => tr("Solicitud de reembolso"),
    desc:  (p) => `$${p.amount} — ${p.reason || tr("Cliente solicitó reembolso")}`,
    action:() => ({ label: 'Gestionar', path: '/profile' }) },

  // --- Courses ---
  course_approved:      { category: CATEGORIES.COURSES, priority: PRIORITIES.HIGH, icon: 'CheckCircle2',
    title: (p) => tr("Curso aprobado: {0}", p.courseName),
    desc:  () => tr("Ya está visible en tu Academy"),
    action:() => ({ label: tr("Ver curso"), path: '/profile' }) },
  course_rejected:      { category: CATEGORIES.COURSES, priority: PRIORITIES.HIGH, icon: 'XCircle',
    title: (p) => tr("Curso rechazado: {0}", p.courseName),
    desc:  (p) => p.adminNote || tr("Revisa los comentarios del admin"),
    action:() => ({ label: tr("Ver detalles"), path: '/profile' }) },
  course_new_student:   { category: CATEGORIES.COURSES, priority: PRIORITIES.MEDIUM, icon: 'UserPlus',
    title: (p) => tr("Nuevo alumno: {0}", p.studentName),
    desc:  (p) => tr("Se inscribió en \"{0}\"", p.courseName),
    action:() => ({ label: tr("Ver alumnos"), path: '/profile' }) },
  course_new_review:    { category: CATEGORIES.COURSES, priority: PRIORITIES.MEDIUM, icon: 'Star',
    title: (p) => tr("Nueva reseña: {0}⭐", p.rating),
    desc:  (p) => p.preview ? `"${p.preview}"` : tr("En \"{0}\"", p.courseName),
    action:() => ({ label: tr("Ver reseña"), path: '/profile' }) },

  // --- Shop ---
  shop_new_order:       { category: CATEGORIES.SHOP, priority: PRIORITIES.HIGH, icon: 'ShoppingBag',
    title: (p) => tr("Nueva orden #{0}", p.orderId),
    desc:  (p) => `${p.productName} · $${p.amount} · ${p.buyerName}`,
    action:() => ({ label: tr("Preparar envío"), path: '/profile' }) },
  shop_order_shipped:   { category: CATEGORIES.SHOP, priority: PRIORITIES.MEDIUM, icon: 'Truck',
    title: (p) => tr("Orden #{0} enviada", p.orderId),
    desc:  (p) => `Tracking: ${p.tracking || tr("sin número")}`,
    action:() => ({ label: tr("Ver orden"), path: '/profile' }) },
  shop_order_delivered: { category: CATEGORIES.SHOP, priority: PRIORITIES.MEDIUM, icon: 'PackageCheck',
    title: (p) => tr("Orden #{0} entregada", p.orderId),
    desc:  () => tr("El cliente recibió el producto"),
    action:() => ({ label: tr("Ver"), path: '/profile' }) },
  shop_return_requested:{ category: CATEGORIES.SHOP, priority: PRIORITIES.HIGH, icon: 'Undo2',
    title: (p) => tr("Devolución solicitada #{0}", p.orderId),
    desc:  (p) => p.reason || tr("El cliente solicitó una devolución"),
    action:() => ({ label: 'Gestionar', path: '/profile' }) },

  // --- Sponsorship ---
  sponsor_new_offer:    { category: CATEGORIES.SPONSORSHIP, priority: PRIORITIES.HIGH, icon: 'Building2',
    title: (p) => tr("Nueva oferta de {0}", p.brandName),
    desc:  (p) => tr("${0} por {1}", p.amount, p.duration || tr("contrato")),
    action:() => ({ label: tr("Ver oferta"), path: '/profile' }) },
  sponsor_offer_accepted:{category: CATEGORIES.SPONSORSHIP, priority: PRIORITIES.HIGH, icon: 'Handshake',
    title: (p) => tr("{0} aceptó tu oferta", p.barberName),
    desc:  (p) => `$${p.amount} · ${p.duration || tr("contrato activo")}`,
    action:() => ({ label: tr("Ver contrato"), path: '/brand' }) },
  sponsor_offer_rejected:{category: CATEGORIES.SPONSORSHIP, priority: PRIORITIES.MEDIUM, icon: 'XCircle',
    title: (p) => tr("{0} rechazó tu oferta", p.barberName),
    desc:  (p) => p.reason || tr("Puedes ajustar los términos"),
    action:() => ({ label: tr("Ver en Marca"), path: '/brand' }) },
  sponsor_payment:      { category: CATEGORIES.SPONSORSHIP, priority: PRIORITIES.HIGH, icon: 'DollarSign',
    title: (p) => tr("Pago de patrocinio: ${0}", p.amount),
    desc:  (p) => tr("De {0}", p.brandName),
    action:() => ({ label: tr("Ver"), path: '/profile' }) },

  // --- Barberías (BS1) ---
  shop_invite:          { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'Store',
    title: (p) => tr("{0} te invita a trabajar en su local", p.shop_name || tr("Una barbería")),
    desc:  (p) => (p.mode === 'rent'
      ? tr("Por alquiler: {0}. Sin % por cita. Acepta o rechaza en Mi barbería.", rentLabel(p.rent))
      : tr("Cederías {0}% de cada cita al local. Acepta o rechaza en Mi barbería.", Number(p.pct || 0))),
    action:() => ({ label: 'Responder', path: '/settings/barberia' }) },
  shop_invite_accepted: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'Store',
    title: (p) => tr("@{0} aceptó tu invitación", p.barber_username),
    desc:  (p) => (p.mode === 'rent' ? tr("Ya es parte de tu barbería por alquiler: {0}", rentLabel(p.rent)) : tr("Ya es parte de tu barbería con {0}% para el local", Number(p.pct || 0))),
    action:() => ({ label: tr("Ver panel"), path: '/shop' }) },
  shop_invite_declined: { category: CATEGORIES.BOOKING, priority: PRIORITIES.LOW, icon: 'Store',
    title: (p) => tr("@{0} rechazó tu invitación", p.barber_username),
    desc:  () => tr("Puedes invitarlo de nuevo más adelante"),
    action:() => ({ label: tr("Ver panel"), path: '/shop' }) },
  // Fase A (sep-2026) — Solicitudes de trabajo barbería ↔ barbero (sin vínculo automático).
  work_request_received: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'Handshake',
    title: (p) => (p.from_role === 'shop' ? tr("{0} te envió una solicitud de trabajo", p.from_name || tr("Una barbería")) : tr("{0} quiere trabajar en tu barbería", p.from_name || tr("Un barbero"))),
    desc:  (p) => (p.message ? `«${p.message}»` : tr("Acepta o rechaza en Solicitudes.")),
    action:() => ({ label: 'Responder', path: '/solicitudes' }) },
  work_request_accepted: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'Handshake',
    title: (p) => tr("{0} aceptó tu solicitud", p.by_name || `@${p.by_username}`),
    desc:  () => tr("Queden en persona para acordar condiciones."),
    action:() => ({ label: tr("Ver solicitudes"), path: '/solicitudes?tab=enviadas' }) },
  work_request_rejected: { category: CATEGORIES.BOOKING, priority: PRIORITIES.LOW, icon: 'Handshake',
    title: (p) => tr("{0} rechazó tu solicitud", p.by_name || `@${p.by_username}`),
    desc:  () => tr("Puedes enviar otra más adelante."),
    action:() => ({ label: tr("Ver solicitudes"), path: '/solicitudes?tab=enviadas' }) },
  // Fase C/D — Acuerdo de trabajo (registro interno) y Vincular.
  work_agreement_received: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'FileText',
    title: (p) => tr("{0} te envió un Acuerdo de trabajo", p.shop_name || tr("Una barbería")),
    desc:  (p) => (p.model === 'rent' ? tr("Modelo de alquiler. Revísalo y acepta o pide cambios.") : tr("Cederías {0}% de cada cita al local. Revísalo y acepta o pide cambios.", Number(p.pct || 0))),
    action:() => ({ label: tr("Revisar"), path: '/solicitudes' }) },
  work_agreement_accepted: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'FileText',
    title: (p) => tr("{0} aceptó el Acuerdo de trabajo", p.barber_name || `@${p.barber_username}`),
    desc:  () => tr("Ya puedes Vincular al barbero a tu barbería."),
    action:() => ({ label: tr("Vincular"), path: '/solicitudes?tab=enviadas' }) },
  work_agreement_rejected: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'FileText',
    title: (p) => tr("{0} pidió cambios al acuerdo", p.barber_name || `@${p.barber_username}`),
    desc:  (p) => (p.message ? `«${p.message}»` : tr("Revisa y envía un nuevo acuerdo.")),
    action:() => ({ label: tr("Ver"), path: '/solicitudes?tab=enviadas' }) },
  work_agreement_linked: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'Store',
    title: (p) => tr("Ya formas parte de {0}", p.shop_name || tr("la barbería")),
    desc:  (p) => (p.model === 'rent' ? tr("Vínculo creado con modelo de alquiler.") : tr("Vínculo creado: cedes {0}% de cada cita al local.", Number(p.pct || 0))),
    action:() => ({ label: tr("Mi barbería"), path: '/settings/barberia' }) },
  shop_agreement_proposed: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Percent',
    title: (p) => (p.mode === 'rent'
      ? tr("{0} propone pasar a alquiler: {1}", p.shop_name || tr("Tu barbería"), rentLabel(p.rent))
      : tr("{0} propone cambiar tu % a {1}%", p.shop_name || tr("Tu barbería"), Number(p.pct || 0))),
    desc:  (p) => (p.previous_mode === 'rent'
      ? tr("Hoy pagas alquiler sin % por cita. Nada cambia hasta que aceptes.")
      : tr("Hoy cedes {0}%{1}. Nada cambia hasta que aceptes.", Number(p.previous || 0), Number(p.per_service_count || 0) > 0 ? ` · ${p.per_service_count} servicio(s) con % propio` : '')),
    action:() => ({ label: 'Responder', path: '/settings/barberia' }) },
  shop_agreement_accepted: { category: CATEGORIES.MONEY, priority: PRIORITIES.MEDIUM, icon: 'Percent',
    title: (p) => (p.mode === 'rent' ? tr("@{0} aceptó el alquiler", p.barber_username || tr("barbero")) : tr("@{0} aceptó el nuevo {1}%", p.barber_username || tr("barbero"), Number(p.pct || 0))),
    desc:  (p) => (p.mode === 'rent' ? tr("{0}. Desde ahora no cede % por cita; el libro de renta arranca hoy.", rentLabel(p.rent)) : tr("Antes {0}%. Aplica a las citas que se completen desde ahora.", Number(p.previous || 0))),
    action:() => ({ label: tr("Ver panel"), path: '/shop' }) },
  shop_agreement_rejected: { category: CATEGORIES.MONEY, priority: PRIORITIES.MEDIUM, icon: 'Percent',
    title: (p) => (p.mode === 'rent' ? tr("@{0} rechazó el alquiler propuesto", p.barber_username || tr("barbero")) : tr("@{0} rechazó el {1}% propuesto", p.barber_username || tr("barbero"), Number(p.pct || 0))),
    desc:  (p) => (p.previous_mode === 'rent' ? tr("Sigue vigente el alquiler actual. Puedes proponer otro acuerdo.") : tr("Sigue vigente el {0}%. Puedes proponer otro acuerdo.", Number(p.previous || 0))),
    action:() => ({ label: tr("Ver panel"), path: '/shop' }) },
  shop_pct_changed:     { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Percent',
    title: (p) => tr("{0} cambió tu % a {1}%", p.shop_name || tr("Tu barbería"), Number(p.pct || 0)),
    desc:  (p) => tr("Antes {0}%. Aplica a las citas que se paguen desde ahora.", Number(p.previous || 0)),
    action:() => ({ label: tr("Ver"), path: '/settings/barberia' }) },
  shop_affiliation_ended: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'Store',
    title: (p) => p.ended_by_shop ? tr("{0} terminó tu afiliación", p.by_name || tr("La barbería")) : tr("@{0} salió de tu barbería", p.by_username),
    desc:  (p) => p.ended_by_shop ? tr("Vuelves a cobrar como independiente") : tr("Sus próximas citas se pagan como independiente"),
    action:(p) => ({ label: tr("Ver"), path: p.ended_by_shop ? '/settings/barberia' : '/shop' }) },

  // --- Booking ---
  booking_new:          { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarPlus',
    title: (p) => p.by_shop ? tr("{0} agendó una cita en tu agenda: {1}", p.shop_name || tr("Tu barbería"), p.clientName || tr("Cliente")) : p.for_shop ? tr("Nueva cita de {0}: {1}", p.barber_name || tr("tu barbero"), p.clientName || tr("Cliente")) : tr("Nueva cita con {0}", p.clientName || tr("Cliente")),
    desc:  (p) => `${p.service_name || p.service || tr("Servicio")} · ${_when(p)}${p.paid ? ` · ${tr("Pagada")} ${fmtMoney(Number(p.amount || p.price || 0))}` : ''}`,
    action:(p) => p.for_shop ? ({ label: tr("Ver barbería"), path: '/shop' }) : ({ label: tr("Ver agenda"), path: _agendaPath(p) }) },
  // Recordatorio al CLIENTE (24 h con correo · 2 h solo campana) — `hours_before` lo emite el backend; sin él es el aviso legacy del barbero.
  // Recibo de inscripción / pase (Paquete 3): desglose en montos, nunca porcentaje.
  tournament_entry_receipt: { category: CATEGORIES.MONEY, priority: PRIORITIES.MEDIUM, icon: 'Receipt',
    title: (p) => tr("Recibo: inscripción a {0}", p.name || tr("torneo")),
    desc:  (p) => Number(p.service_fee) > 0 ? tr("Inscripción ${0} + cargo por servicio ${1} = ${2}", Number(p.base).toFixed(2), Number(p.service_fee).toFixed(2), Number(p.total).toFixed(2)) : tr("Total pagado: ${0}", Number(p.total).toFixed(2)),
    action:(p) => ({ label: tr("Ver torneo"), path: `/tournaments/${p.ref_id}` }) },
  league_entry_receipt: { category: CATEGORIES.MONEY, priority: PRIORITIES.MEDIUM, icon: 'Receipt',
    title: (p) => tr("Recibo: pase de {0}", p.name || tr("liga")),
    desc:  (p) => Number(p.service_fee) > 0 ? tr("Pase ${0} + cargo por servicio ${1} = ${2}", Number(p.base).toFixed(2), Number(p.service_fee).toFixed(2), Number(p.total).toFixed(2)) : tr("Total pagado: ${0}", Number(p.total).toFixed(2)),
    action:() => ({ label: tr("Ver ligas"), path: '/leagues' }) },
  booking_reminder:     { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'Clock',
    title: (p) => p.hours_before ? (Number(p.hours_before) <= 2 ? tr("Tu cita es en 2 horas") : (p.deadline ? tr("Tu cita se acerca") : tr("Tu cita es mañana"))) : tr("Recordatorio de cita"),
    desc:  (p) => p.hours_before ? `${tr("{0} con {1} · {2}", p.service_name || tr("Servicio"), p.barber_name || tr("tu barbero"), _when(p))}${p.deadline ? ` · ${tr("cancela sin cargo hasta {0}", _fmtDeadline(p.deadline))}` : ''}` : `${p.clientName || ''} · ${p.when || _when(p)}`,
    action:(p) => p.hours_before ? ({ label: tr("Ver mi cita"), path: '/reservas' }) : ({ label: tr("Ver"), path: _agendaPath(p) }) },
  // CRÍTICO 21-oct-2026 — el barbero te asignó una cita (walk-in → tu cuenta): aparece en Mis citas con acceso a pagar.
  booking_assigned:     { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarPlus',
    title: (p) => tr("Nueva cita a tu nombre con {0}", p.barber_name || tr("tu barbero")),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${_when(p)}${p.can_pay ? ` · ${tr("Por pagar")} $${Number(p.amount_due || 0).toFixed(2)}` : ''}`,
    action:(p) => p.can_pay ? ({ label: tr("Pagar ahora"), path: `/reservas?pay=${p.booking_id}` }) : ({ label: tr("Ver mi cita"), path: '/reservas' }) },
  booking_unassigned:   { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CalendarX',
    title: (p) => tr("Una cita dejó de estar a tu nombre"),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${_when(p)}${p.barber_name ? ` · ${p.barber_name}` : ''}`,
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },
  booking_rescheduled:  { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarPlus',
    title: (p) => p.by_shop ? tr("{0} movió una cita: {1}", p.shop_name || tr("Tu barbería"), p.clientName || tr("Cliente")) : p.for_shop ? tr("Cita reprogramada ({0}): {1}", p.barber_name || tr("barbero"), p.clientName || tr("Cliente")) : tr("Cita reprogramada: {0}", p.clientName || tr("Cliente")),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${_when(p)}${p.old_date ? ` (antes ${p.old_date} ${p.old_start_time || ''})` : ''}`,
    action:(p) => p.for_shop ? ({ label: tr("Ver barbería"), path: '/shop' }) : ({ label: tr("Ver agenda"), path: _agendaPath(p) }) },
  // Parte 1 (founder 14-oct-2026) — el barbero/barbería cambió los servicios de la cita del cliente.
  booking_services_changed: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'Scissors',
    title: (p) => tr("Servicios de tu cita actualizados"),
    desc:  (p) => `${(p.services || []).map((s) => `${s.name} $${Number(s.price || 0).toFixed(2)}`).join(' + ') || p.service_name || tr("Servicio")} · ${tr("Nuevo total")} $${Number(p.total || 0).toFixed(2)} · ${_when(p)}${p.barber_name ? ` · ${p.barber_name}` : ''}`,
    action:(p) => ({ label: tr("Ver mi cita"), path: '/reservas' }) },
  booking_cancelled:    { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CalendarX',
    title: (p) => p.by_shop ? tr("{0} canceló una cita: {1}", p.shop_name || tr("Tu barbería"), p.clientName || tr("Cliente")) : p.for_shop ? tr("Cita cancelada ({0}): {1}", p.barber_name || tr("barbero"), p.clientName || tr("Cliente")) : tr("Cita cancelada: {0}", p.clientName || tr("Cliente")),
    desc:  (p) => `${p.reason || tr("El cliente canceló")}${p.date ? ` · ${_when(p)}` : ''}`,
    action:(p) => p.for_shop ? ({ label: tr("Ver barbería"), path: '/shop' }) : ({ label: tr("Ver agenda"), path: _agendaPath(p) }) },
  // Arreglo 4 (oct-2026) — pago con tarjeta recibido (QR) y fondos liberados a Disponible.
  booking_paid:         { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'CreditCard',
    title: (p) => p.for_shop ? tr("Pago recibido ({0}): ${1}", p.barber_name || tr("barbero"), Number(p.amount || 0).toFixed(2)) : tr("Pago recibido: ${0}", Number(p.amount || 0).toFixed(2)),
    desc:  (p) => `${p.clientName || tr("Cliente")} · ${p.service_name || tr("Servicio")}${Number(p.tip) > 0 ? ` · ${tr("propina")} ${fmtMoney(p.tip)}` : ''} · ${p.method === 'qr' ? tr("QR con tarjeta") : p.method === 'cash' ? tr("Efectivo") : p.method === 'prepay' ? tr("Pago adelantado") : tr("Tarjeta")}`,
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  payout_available:     { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Wallet',
    title: (p) => tr("${0} ya disponibles", Number(p.amount || 0).toFixed(2)),
    desc:  () => tr("Stripe liberó el pago de tu cita · se envía a tu cuenta con el pago diario"),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  // Founder 14-oct-2026 — cita pagada completada sola al pasar su hora de fin.
  booking_auto_completed: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Wallet',
    title: (p) => tr("Tu cita con {0} se completó y tu pago está en camino", p.clientName || tr("tu cliente")),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${p.date || ''} ${p.start_time || ''}${Number(p.amount) > 0 ? ` · ${fmtMoney(p.amount)}` : ''}`,
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  // Founder 14-oct-2026 — pago bloqueado por cuenta de pagos incompleta (una vez + recordatorio 72 h).
  connect_payout_blocked: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'ShieldAlert',
    title: (p) => tr("Tienes {0} esperando: completa tu cuenta de pagos", fmtMoney(p.amount)),
    desc:  (p) => p.reminder ? tr("Recordatorio · tu dinero sigue esperando que completes tu cuenta de pagos") : tr("Tarda unos minutos · en cuanto Stripe la active, el pago sale solo"),
    action:() => ({ label: tr("Completar mi cuenta de pagos"), path: '/money' }) },
  connect_deadline:     { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'ShieldAlert',
    title: (p) => tr("Stripe te pide datos antes del {0}", p.deadline || ''),
    desc:  () => tr("Si no los completas, Stripe pausará tus cobros o depósitos"),
    action:() => ({ label: tr("Completar mi cuenta de pagos"), path: '/money' }) },
  booking_manual:       { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarPlus',
    title: (p) => tr("{0} te agendó una cita", p.barber_name || tr("Tu barbero")),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${p.date || ''} ${p.start_time || ''}`,
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },
  // Pago adelantado (Booksy Pay, jun-2026) — el cliente pagó desde "Mis citas".
  booking_prepaid:      { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CreditCard',
    title: (p) => tr("{0} pagó su cita por adelantado", p.clientName || tr("Un cliente")),
    desc:  (p) => tr("{0} · {1} {2} · ${3}{4} · solo marca la cita como completada, sin cobrar", p.service_name || tr("Servicio"), p.date || '', p.start_time || '', Number(p.amount || 0).toFixed(2), Number(p.tip) > 0 ? ` (propina ${fmtMoney(p.tip)})` : ''),
    action:() => ({ label: tr("Ver agenda"), path: '/profile' }) },
  // --- Deploy 5 · Horario Premium / reembolsos ---
  booking_premium_request: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarPlus',
    title: (p) => tr("Nueva solicitud premium de {0}", p.clientName || tr("un cliente")),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${p.date || ''} ${p.start_time || ''} · ${tr("total")} ${fmtMoney(p.total)} (${p.label || tr("recargo")})`,
    action:() => ({ label: tr("Ver solicitudes"), path: '/profile?tab=agenda&requests=1' }) },
  booking_premium_approved: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarPlus',
    title: (p) => tr("{0} aprobó tu cita premium", p.barber_name || tr("Tu barbero")),
    desc:  (p) => tr("Paga ${0} en {1} min para confirmar · {2} {3}", Number(p.total || 0).toFixed(2), p.minutes || 30, p.date || '', p.start_time || ''),
    action:(p) => ({ label: tr("Pagar ahora"), path: p.booking_id ? `/reservas?pay=${p.booking_id}` : '/reservas' }) },
  booking_premium_rejected: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CalendarX',
    title: (p) => tr("{0} no pudo aceptar tu cita premium", p.barber_name || tr("Tu barbero")),
    desc:  (p) => p.reason || `${p.service_name || tr("Servicio")} · ${p.date || ''} ${p.start_time || ''}`,
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },
  // Protección contra inasistencias v2 · B.3 (sep-2026) — cargo a la tarjeta guardada
  booking_fee_charged: { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CreditCard',
    title: (p) => tr("Cargo de ${0} por {1}", Number(p.amount || 0).toFixed(2), p.fee_type === 'no_show' ? tr("inasistencia") : 'cancelación tardía'),
    desc:  (p) => tr("{0} · {1} · tarjeta ···· {2} · según la política que aceptaste al reservar", p.barber_name || tr("Tu barbero"), p.service_name || tr("Servicio"), p.card_last4 || '????'),
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },
  booking_fee_auto_charged: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CreditCard',
    title: (p) => tr("Cargo automático de ${0} cobrado", Number(p.amount || 0).toFixed(2)),
    desc:  (p) => `${p.clientName || tr("Cliente")} · ${p.fee_type === 'no_show' ? tr("inasistencia") : tr("cancelación tardía")} · ${p.service_name || tr("Servicio")}`,
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  // Fase 1 Reseñas (sep-2026)
  review_new: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'Star',
    title: (p) => tr("Nueva reseña de {0} · {1}★", p.clientName || tr("un cliente"), p.rating || '?'),
    desc:  (p) => p.hasMedia ? tr("Con foto o video · ya está en tu perfil y en el feed. Puedes responder una vez.") : tr("Ya está en tu perfil. Puedes responder una vez."),
    action:() => ({ label: tr("Ver y responder"), path: '/profile' }) },
  // Regla de notificaciones (founder 14-oct-2026) — avisos SOLO campana nuevos.
  review_request: { category: CATEGORIES.BOOKING, priority: PRIORITIES.LOW, icon: 'Star',
    title: (p) => tr("¿Cómo te fue con {0}?", p.barber_name || tr("tu barbero")),
    desc:  (p) => tr("Califica tu cita de {0} · tu reseña ayuda a otros clientes", p.service_name || tr("Servicio")),
    action:() => ({ label: tr("Calificar esta cita"), path: '/reservas' }) },
  rebook_nudge: { category: CATEGORIES.BOOKING, priority: PRIORITIES.LOW, icon: 'CalendarPlus',
    title: (p) => tr("¿Toca retoque? Vuelve a reservar con {0}", p.barber_name || tr("tu barbero")),
    desc:  (p) => tr("Han pasado 3 semanas desde tu último {0}", p.service_name || tr("servicio")),
    action:(p) => ({ label: tr("Reservar"), path: p.rebook_path || (p.barber_username ? `/barber/${p.barber_username}/reservar` : '/reservas') }) },
  create_password_nudge: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.LOW, icon: 'KeyRound',
    title: () => tr("Crea tu contraseña para mantener tu cuenta segura"),
    desc:  (p) => tr("Tu cita de {0} quedó completada. Con contraseña entras desde cualquier dispositivo y guardas tus tarjetas.", p.service_name || tr("hoy")),
    action:() => ({ label: tr("Crear contraseña"), path: '/crear-contrasena?next=%2Freservas' }) },
  no_show_reverted: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CalendarCheck',
    title: () => tr("Tu cita volvió a quedar como asistida"),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${_when(p)}${Number(p.fee_refunded) > 0 ? ` · ${tr("cargo devuelto")} ${fmtMoney(p.fee_refunded)}` : ''}`,
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },
  bank_deposit: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'Landmark',
    title: (p) => tr("Tu pago de {0} llegó a tu banco", fmtMoney(p.amount)),
    desc:  () => tr("Stripe depositó el dinero en tu cuenta bancaria"),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  debt_registered: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'AlertTriangle',
    title: (p) => tr("Deuda de {0} registrada · retiros bloqueados", fmtMoney(p.amount)),
    desc:  () => tr("Se descuenta sola de tus próximos cobros; hasta cubrirla no puedes retirar"),
    action:() => ({ label: tr("Ver Mi Dinero"), path: '/money' }) },
  membership_charged: { category: CATEGORIES.MONEY, priority: PRIORITIES.MEDIUM, icon: 'BadgeCheck',
    title: (p) => tr("Membresía renovada: {0}", fmtMoney(p.amount)),
    desc:  (p) => p.plan_name ? tr("Plan {0} · cobro exitoso", p.plan_name) : tr("Cobro exitoso"),
    action:(p) => ({ label: tr("Ver membresía"), path: p.scope === 'shop' ? '/shop/membresia' : '/membresia' }) },
  membership_tier_changed: { category: CATEGORIES.MONEY, priority: PRIORITIES.MEDIUM, icon: 'BadgeCheck',
    title: (p) => tr("Tu nivel cambió a {0}", p.plan_name || ''),
    desc:  (p) => p.prev_plan ? tr("Antes: {0}", p.prev_plan) : '',
    action:(p) => ({ label: tr("Ver membresía"), path: p.scope === 'shop' ? '/shop/membresia' : '/membresia' }) },
  membership_payment_failed: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'ShieldAlert',
    title: () => tr("No pudimos cobrar tu membresía"),
    desc:  () => tr("Actualiza tu método de pago para no perder los beneficios"),
    action:(p) => ({ label: tr("Actualizar método de pago"), path: p.scope === 'shop' ? '/shop/membresia' : '/membresia' }) },
  agenda_digest: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CalendarDays',
    title: (p) => p.for_shop ? tr("Mañana: {0} citas del equipo", p.count) : tr("Mañana tienes {0} citas", p.count),
    desc:  (p) => `${p.first || ''}–${p.last || ''}${(p.clients || []).length ? ` · ${(p.clients || []).join(', ')}` : ''}`,
    action:(p) => p.for_shop ? ({ label: tr("Ver agenda del equipo"), path: `/shop/agenda?date=${p.date || ''}` }) : ({ label: tr("Ver agenda"), path: _agendaPath(p) }) },
  shop_barber_joined: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.MEDIUM, icon: 'Users',
    title: (p) => tr("{0} se unió a tu barbería", p.barber_name || tr("Un barbero")),
    desc:  () => tr("Ya aparece en tu equipo y en la agenda del local"),
    action:() => ({ label: tr("Ver barbería"), path: '/shop' }) },
  shop_barber_left: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.MEDIUM, icon: 'Users',
    title: (p) => tr("{0} dejó tu barbería", p.barber_name || tr("Un barbero")),
    desc:  () => tr("Sus citas futuras siguen siendo suyas; ya no aparece en tu equipo"),
    action:() => ({ label: tr("Ver barbería"), path: '/shop' }) },
  review_reply: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'MessageSquare',
    title: (p) => tr("{0} respondió a tu reseña", p.barberName || tr("El profesional")),
    desc:  () => tr("Su respuesta es pública junto a tu reseña"),
    action:(p) => ({ label: tr("Ver respuesta"), path: p.barberUsername ? `/barber/${p.barberUsername}` : '/reservas' }) },
  // Oct-2026 — tarjetas de regalo · campañas de barbería · avisos masivos · solicitudes de torneo · compras Tienda
  gift_card_received: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'Gift',
    title: (p) => tr("{0} te regaló {1} en Ziyup", p.fromName || tr("Alguien"), p.amount || tr("una tarjeta")),
    desc:  (p) => tr("Código {0}{1} · úsalo al pagar tu próxima cita", p.code || '', p.message ? ` · «${p.message}»` : ''),
    action:() => ({ label: tr("Ver mi tarjeta"), path: '/regalo/tarjeta' }) },
  shop_campaign: { category: CATEGORIES.SOCIAL, priority: PRIORITIES.MEDIUM, icon: 'Megaphone',
    title: (p) => `${p.shopName || (p.senderKind === 'barber' ? tr("Tu barbero") : tr("Tu barbería"))}: ${p.title || tr("Novedades")}`,
    desc:  (p) => `${p.message || ''}${p.promoCode ? tr("· Código {0}", p.promoCode) : ''}${p.validUntil ? ` · ${tr("hasta {0}", p.validUntil)}` : ''}`,
    action:(p) => ({ label: p.senderKind === 'barber' ? tr("Ver barbero") : tr("Ver barbería"), path: p.shopUsername ? (p.senderKind === 'barber' ? `/barber/${p.shopUsername}` : `/shop/${p.shopUsername}`) : '/barberos/cerca' }) },
  dispute_opened: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'ShieldAlert',
    title: (p) => tr("Contracargo de ${0}", p.amount || ''),
    desc:  (p) => tr("Un cliente disputó un cobro. Tarifa de disputa ${0}. Puedes subir evidencia en Mi Dinero hasta el {1}.", p.fee || '15.00', p.due || ''),
    action:(p) => ({ label: tr("Ver en Mi Dinero"), path: p.path || '/professional/finanzas' }) },
  dispute_won: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'ShieldCheck',
    title: (p) => tr("Disputa ganada: ${0} recuperados", p.amount || ''),
    desc:  () => tr("El banco falló a tu favor. El monto y la tarifa por contestar vuelven a tu saldo."),
    action:(p) => ({ label: tr("Ver en Mi Dinero"), path: p.path || '/professional/finanzas' }) },
  dispute_lost: { category: CATEGORIES.MONEY, priority: PRIORITIES.HIGH, icon: 'ShieldX',
    title: (p) => tr("Disputa perdida: ${0}", p.amount || ''),
    desc:  () => tr("El banco falló a favor del cliente. El monto disputado y las tarifas no se devuelven."),
    action:(p) => ({ label: tr("Ver en Mi Dinero"), path: p.path || '/professional/finanzas' }) },
  card_sign_outdated: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.MEDIUM, icon: 'Printer',
    title: () => tr("Tus precios cambiaron, vuelve a imprimir tu letrero."),
    desc:  () => tr("Tu letrero de precios con tarjeta y efectivo ya no coincide con tus servicios."),
    action:(p) => ({ label: tr("Imprimir letrero"), path: p.path || '/ajustes/letrero' }) },
  claude_access_grace: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'Bot',
    title: (p) => tr("Tu conector Claude cambia el {0}", p.until || ''),
    desc:  (p) => (p.identity === 'shop'
      ? tr("El asistente Claude ahora es un beneficio de los planes de barbería que lo incluyen. Seguirá funcionando hasta esa fecha (30 días de gracia).")
      : tr("El conector Claude ahora es un beneficio de los niveles de membresía que lo incluyen. Seguirá funcionando hasta esa fecha (30 días de gracia).")),
    action:(p) => ({ label: p.identity === 'shop' ? tr("Ver planes") : tr("Ver niveles"), path: p.path || '/membresia' }) },
  admin_broadcast: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'BellRing',
    title: (p) => p.title || tr("Aviso de Ziyup"),
    desc:  (p) => p.message || '',
    action:(p) => (p.link ? { label: tr("Abrir"), path: p.link } : null) },
  tournament_request: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => tr("{0} solicita un torneo", p.shopName || tr("Una barbería")),
    desc:  (p) => `${p.name || ''} · ${p.city || ''}${p.prizeMoney ? tr("· con premio en dinero") : ''}`,
    action:() => ({ label: tr("Revisar"), path: '/admin?tab=tournaments' }) },
  tournament_request_decision: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'Trophy',
    title: (p) => tr("Tu solicitud de torneo «{0}» fue {1}", p.name || '', p.status === 'approved' ? 'aprobada' : 'rechazada'),
    desc:  (p) => p.note || (p.status === 'approved' ? tr("Ziyup lo creará y publicará pronto") : tr("Puedes enviar una nueva propuesta")),
    action:() => ({ label: tr("Ver mis solicitudes"), path: '/shop/solicitar-torneo' }) },
  marketplace_order_paid: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'ShoppingBag',
    title: (p) => tr("Compra confirmada · ${0}", Number(p.total || 0).toFixed(2)),
    desc:  (p) => tr("{0} pedido(s){1}", p.orders || 1, Array.isArray(p.orderNumbers) ? ` · ${p.orderNumbers.join(', ')}` : ''),
    action:() => ({ label: tr("Ir a la Tienda"), path: '/shop' }) },
  review_reported: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'Flag',
    title: () => tr("Reseña reportada"),
    desc:  (p) => tr("Motivo: {0} · Revisa con las reglas objetivas", p.reason || tr("sin especificar")),
    action:() => ({ label: tr("Abrir Reportes"), path: '/admin?tab=reports' }) },
  content_reported: { category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'Flag',
    title: (p) => tr("Contenido reportado ({0})", p.targetType || tr("post")),
    desc:  (p) => tr("Motivo: {0} · Revisa con las reglas objetivas", p.reason || tr("sin especificar")),
    action:() => ({ label: tr("Abrir Reportes"), path: '/admin?tab=reports' }) },
  booking_premium_expired: { category: CATEGORIES.BOOKING, priority: PRIORITIES.MEDIUM, icon: 'CalendarX',
    title: () => tr("Tu aprobación premium venció sin pago"),
    desc:  (p) => tr("{0} · {1} {2} — vuelve a solicitar el horario", p.service_name || tr("Servicio"), p.date || '', p.start_time || ''),
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },
  booking_refunded:     { category: CATEGORIES.BOOKING, priority: PRIORITIES.HIGH, icon: 'CalendarX',
    title: (p) => p.kind === 'cash_void' ? tr("Tu cobro en efectivo fue anulado") : tr("Reembolso de ${0} en camino", Number(p.amount || 0).toFixed(2)),
    desc:  (p) => `${p.service_name || tr("Servicio")} · ${p.barber_name || ''}${p.kind === 'cash_void' ? '' : tr("· 5-10 días hábiles")}`,
    action:() => ({ label: tr("Ver mis citas"), path: '/reservas' }) },

  // --- Live ---
  live_tournament_started:{category: CATEGORIES.LIVE, priority: PRIORITIES.MEDIUM, icon: 'Trophy',
    title: () => tr("Torneo en vivo"),
    desc:  (p) => p.tournamentName,
    action:(p) => ({ label: tr("Ver"), path: p.tournamentId ? `/tournament/${p.tournamentId}` : '/tournaments' }) },

  // --- System ---
  system_login_alert:   { category: CATEGORIES.SYSTEM, priority: PRIORITIES.MEDIUM, icon: 'Shield',
    title: () => tr("Nuevo inicio de sesión"),
    desc:  (p) => `${p.device || tr("Dispositivo")} · ${p.location || tr("Ubicación desconocida")}`,
    action:() => ({ label: tr("Revisar"), path: '/profile' }) },
  system_password_change:{category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'KeyRound',
    title: () => tr("Contraseña actualizada"),
    desc:  () => tr("Si no fuiste tú, contacta soporte"),
    action:() => ({ label: tr("Ver"), path: '/profile' }) },
  system_verification_update:{category: CATEGORIES.SYSTEM, priority: PRIORITIES.HIGH, icon: 'BadgeCheck',
    title: (p) => p.verified ? tr("Verificación aprobada") : tr("Verificación rechazada"),
    desc:  (p) => p.verified ? tr("Ahora tienes badge de verificado") : (p.reason || tr("Revisa tus documentos")),
    action:() => ({ label: tr("Ver perfil"), path: '/profile' }) }
};

// ============================================================
// Settings por categoría (enable/disable push/in-app)
// ============================================================
export const DEFAULT_SETTINGS = {
  // Por categoría: { inApp: bool, push: bool }
  [CATEGORIES.COMPETITIONS]: { inApp: true, push: true },
  [CATEGORIES.TOURNAMENTS]:  { inApp: true, push: true },
  [CATEGORIES.RANKING]:      { inApp: true, push: false },
  [CATEGORIES.SOCIAL]:       { inApp: true, push: false },
  [CATEGORIES.MONEY]:        { inApp: true, push: true },
  [CATEGORIES.COURSES]:      { inApp: true, push: true },
  [CATEGORIES.SHOP]:         { inApp: true, push: true },
  [CATEGORIES.SPONSORSHIP]:  { inApp: true, push: true },
  [CATEGORIES.BOOKING]:      { inApp: true, push: true },
  [CATEGORIES.LIVE]:         { inApp: true, push: false },
  [CATEGORIES.SYSTEM]:       { inApp: true, push: true }
};

export const getSettings = () => {
  const raw = localStorage.getItem(SETTINGS_KEY);
  if (!raw) return { ...DEFAULT_SETTINGS };
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveSettings = (settings) => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  window.dispatchEvent(new Event('lineage_notifications_updated'));
};

export const updateCategorySetting = (category, patch) => {
  const s = getSettings();
  s[category] = { ...s[category], ...patch };
  saveSettings(s);
};

// ============================================================
// CRUD — write-through to backend + local cache mirror
// ============================================================
const read = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const write = (arr) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  window.dispatchEvent(new Event('lineage_notifications_updated'));
};

// Normalize backend shape → frontend shape (used by hydrate + create).
const _normalize = (n) => {
  if (!n) return n;
  return {
    id: n.id,
    userId: n.user_id ?? n.userId,
    fromUserId: n.from_user_id ?? n.fromUserId,
    type: n.type,
    category: n.category,
    priority: n.priority,
    icon: n.icon,
    params: n.params || {},
    fromName: n.from_name ?? n.fromName ?? (n.params?.fromName ?? null),
    avatarUrl: n.avatar_url ?? n.avatarUrl ?? null,
    thumbnailUrl: n.thumbnail_url ?? n.thumbnailUrl ?? null,
    groupKey: n.group_key ?? n.groupKey ?? null,
    read: !!n.read,
    createdAt: n.created_at ?? n.createdAt,
    readAt: n.read_at ?? n.readAt ?? null,
  };
};

export const getAllNotifications = (userId) => {
  const all = read();
  if (!userId) return all;
  return all.filter((n) => n.userId === userId);
};

export const getUnreadCount = (userId) =>
  getAllNotifications(userId).filter((n) => !n.read).length;

// Crear una notificación.
// payload: { userId (destino), type (key), params (obj), fromName?, avatarUrl?, groupKey? }
//
// Optimistic write-through: localStorage update is synchronous so the UI
// updates instantly; the backend POST runs in the background. On hydration
// the cache is reconciled with the server. The local id may be overwritten
// by the backend id once the POST resolves.
export const createNotification = ({ userId, type, params = {}, fromName, avatarUrl, groupKey }) => {
  if (!userId || !type) return null;
  const def = NOTIFICATION_TYPES[type];
  if (!def) {
    console.warn('Unknown notification type:', type);
    return null;
  }
  const settings = getSettings();
  const catSetting = settings[def.category];
  if (catSetting && !catSetting.inApp) return null; // usuario desactivó la categoría

  const optimisticId = `ntf_opt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const local = {
    id: optimisticId,
    userId,
    type,
    category: def.category,
    priority: def.priority,
    icon: def.icon,
    params,
    fromName: fromName || params.fromName || null,
    avatarUrl: avatarUrl || params.avatarUrl || null,
    groupKey: groupKey || null,
    read: false,
    createdAt: new Date().toISOString(),
    _pending: true,
  };
  const arr = read();
  write([local, ...arr]);

  // Fire-and-forget backend persistence. On success, swap the optimistic id
  // for the real backend id so subsequent mark-read / delete calls hit the
  // server-side record.
  notificationsApi.create({
    target_user_id: userId,
    type,
    category: def.category,
    priority: def.priority,
    icon: def.icon,
    params,
    from_name: fromName || params.fromName || null,
    avatar_url: avatarUrl || params.avatarUrl || null,
    group_key: groupKey || null,
  }).then((be) => {
    const norm = _normalize(be);
    const next = read().map((n) => (n.id === optimisticId
      ? { ...norm, _pending: false } : n));
    write(next);
  }).catch(() => {
    // Mark as not persisted — UI can still show it, but on hydrate it'll be
    // dropped. This is acceptable for ephemeral edge cases (offline, 404, etc.).
  });

  return local;
};

export const markAsRead = async (notifId) => {
  // Optimistic.
  const arr = read().map((n) => (n.id === notifId
    ? { ...n, read: true, readAt: new Date().toISOString() } : n));
  write(arr);
  // Skip backend roundtrip for optimistic (not-yet-persisted) entries.
  if (!notifId || notifId.startsWith('ntf_opt_') || notifId.startsWith('ntf_seed_') ||
      notifId.startsWith('grp_')) {
    return;
  }
  try { await notificationsApi.markRead(notifId); }
  catch { /* rollback would surprise users — leave optimistic state */ }
};

export const markAllAsRead = async (userId) => {
  const now = new Date().toISOString();
  const arr = read().map((n) => (n.userId === userId && !n.read
    ? { ...n, read: true, readAt: now } : n));
  write(arr);
  try { await notificationsApi.markAllRead(); }
  catch { /* offline OK */ }
};

export const deleteNotification = async (notifId) => {
  write(read().filter((n) => n.id !== notifId));
  if (!notifId || notifId.startsWith('ntf_opt_') || notifId.startsWith('ntf_seed_') ||
      notifId.startsWith('grp_')) {
    return;
  }
  try { await notificationsApi.remove(notifId); }
  catch { /* ignore */ }
};

export const clearAllNotifications = async (userId) => {
  write(read().filter((n) => n.userId !== userId));
  try { await notificationsApi.clearAll(); }
  catch { /* offline OK */ }
};

// ============================================================
// Hydration — pulls authoritative server state into local cache.
// Merges with any local seeded/optimistic entries so demo seeds still show.
// ============================================================
export const hydrateNotificationsFromBackend = async (user) => {
  if (!user?.id) return;
  try {
    const res = await notificationsApi.list({ limit: 200 });
    const backendNotifs = (res.items || []).map(_normalize);
    const backendIds = new Set(backendNotifs.map((n) => n.id));
    const local = read();
    // Keep seeds + still-pending optimistic entries that don't have a backend twin.
    const surviving = local.filter((n) => {
      if (backendIds.has(n.id)) return false;
      const isSeed = typeof n.id === 'string' && n.id.startsWith('ntf_seed_');
      const isPending = n._pending === true;
      return isSeed || isPending;
    });
    const merged = [...backendNotifs, ...surviving].sort(
      (a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')
    );
    write(merged);
  } catch {
    /* offline OK */
  }
};

// ============================================================
// Renderizado: resolver title/desc/action ejecutando las funciones
// ============================================================
export const resolveNotification = (notif) => {
  const def = NOTIFICATION_TYPES[notif.type] || {};
  // Hardening: render-time fallback so explicit POSTs that forget to embed
  // `fromName` inside `params` still display correctly (the auto-emit paths
  // already set it; this guards against ad-hoc creators).
  const params = {
    ...(notif.params || {}),
    fromName: notif.params?.fromName || notif.fromName || '',
    clientName: notif.params?.clientName || notif.params?.client_name || notif.fromName || '',
  };
  // Fallback: tipos desconocidos jamás renderizan una fila en blanco.
  const title  = def.title ? def.title(params) : (params.message || tr("Notificación"));
  const desc   = def.desc ? def.desc(params) : '';
  const action = def.action ? def.action(params) : null;
  return { ...notif, title, desc, action };
};

// ============================================================
// Agrupación: notifs del mismo type+groupKey+user en ventana de 24h
// Se agrupan para mostrar como "María y 5 más te dieron like"
// ============================================================
export const groupNotifications = (notifs) => {
  const WINDOW_MS = 24 * 60 * 60 * 1000; // 24h
  // Tipos que NUNCA se agrupan (cada uno lleva info específica e importante)
  const NEVER_GROUP = new Set([
    'tip_received', 'course_sale', 'product_sale', 'refund_request',
    'tournament_registered', 'tournament_round_result',
    // Iter 207 F4 — every tournament notification is actionable with its
    // own deep link: grouping would hide the exact destination.
    'tournament_payment_confirmed', 'tournament_entry_ready',
    'tournament_checkin_open', 'tournament_checkin_done', 'live_session_reminder', 'live_photos_late_review', 'live_photos_judging_open', 'live_results_published', 'live_appeal_resolved', 'live_phase_advanced', 'live_phase_eliminated', 'judge_program_approved', 'judge_program_rejected', 'judge_program_needs_info', 'judge_suspended_shift', 'judge_reserve_called', 'judge_sanction', 'live_champion', 'live_session_assigned', 'live_judge_invite',
    'tournament_match_ready', 'tournament_opponent_revealed',
    'tournament_updated', 'tournament_schedule_changed',
    'tournament_modality_changed',
    'tournament_prize_pending', 'tournament_prize_approved',
    'tournament_prize_paid', 'tournament_reminder', 'tournament_cancelled',
    'tournament_judge_application_approved', 'tournament_judge_application_rejected',
    'tournament_judge_selected', 'tournament_judge_reserve', 'tournament_judge_not_selected',
    'tournament_judge_deficit', 'tournament_judge_deficit_resolved',
    'league_judge_approved', 'league_judge_rejected', 'league_cut_scored',
    'league_champion', 'league_prize_awarded', 'league_equalizer_window',
    'league_phase_open', 'league_phase_closing',
    'league_tiebreak_open', 'league_tiebreak_closing',
    'league_tiebreak_scored', 'league_tiebreak_resolved',
    'shop_new_order', 'shop_return_requested',
    'sponsor_new_offer', 'course_approved', 'course_rejected',
    'system_password_change', 'entered_top_25', 'entered_top_100'
  ]);

  const result = [];
  const used = new Set();
  const sorted = [...notifs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  for (let i = 0; i < sorted.length; i++) {
    if (used.has(sorted[i].id)) continue;
    const current = sorted[i];
    if (NEVER_GROUP.has(current.type)) {
      result.push(current);
      used.add(current.id);
      continue;
    }
    // Buscar similares (mismo type + groupKey) dentro de la ventana
    const matches = [current];
    const baseTime = new Date(current.createdAt).getTime();
    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(sorted[j].id)) continue;
      const other = sorted[j];
      const sameType = other.type === current.type;
      const sameGroup = (other.groupKey || null) === (current.groupKey || null);
      const inWindow = baseTime - new Date(other.createdAt).getTime() <= WINDOW_MS;
      if (sameType && sameGroup && inWindow) {
        matches.push(other);
      }
    }
    matches.forEach((m) => used.add(m.id));
    if (matches.length >= 3) {
      // Agrupado
      result.push({
        ...current,
        id: `grp_${current.id}`,
        grouped: true,
        groupSize: matches.length,
        groupedIds: matches.map((m) => m.id),
        read: matches.every((m) => m.read),
        createdAt: current.createdAt
      });
    } else {
      matches.forEach((m) => result.push(m));
    }
  }
  return result;
};

// ============================================================
// Helpers auxiliares de UI
// ============================================================
export const formatRelativeTime = (iso) => {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'ahora';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 4) return `${w}sem`;
  return new Date(iso).toLocaleDateString(localeTag());
};

export const getCategoryColor = (category) => {
  // Todos los colores vienen del design system (HSL). NO hardcoded.
  switch (category) {
    case CATEGORIES.MONEY:
    case CATEGORIES.COURSES:
    case CATEGORIES.SHOP:
    case CATEGORIES.SPONSORSHIP:
      return 'text-emerald-500';
    case CATEGORIES.COMPETITIONS:
    case CATEGORIES.LIVE:
      return 'text-primary'; // dorado
    case CATEGORIES.TOURNAMENTS:
      return 'text-primary';
    case CATEGORIES.RANKING:
      return 'text-primary';
    case CATEGORIES.SOCIAL:
      return 'text-foreground';
    case CATEGORIES.BOOKING:
      return 'text-primary';
    case CATEGORIES.SYSTEM:
    default:
      return 'text-muted-foreground';
  }
};

export const getPriorityDot = (priority) => {
  if (priority === PRIORITIES.HIGH) return 'bg-primary';   // dorado
  if (priority === PRIORITIES.MEDIUM) return 'bg-foreground/60';
  return 'bg-muted-foreground/40';
};

// ============================================================
// Seed: NO-OP for the closed beta.
//
// Previously this function injected 5–14 fabricated notifications per role
// ("tip recibido de Sarah M.", "fade_master te siguió", etc) so the UI
// didn't feel empty during prototyping. For the beta we want every
// notification a user sees to be REAL — emitted by the backend in response
// to an actual event. So this is now a no-op that simply clears any stale
// seeded entries the user might still have in localStorage from a previous
// session.
// ============================================================
export const seedNotificationsForUser = (user) => {
  if (!user || !user.id) return;
  const key = SEEDED_KEY_PREFIX + user.id;
  // First time we run on this device: wipe legacy seeded entries.
  if (!localStorage.getItem(`${key}_cleaned_v2`)) {
    try {
      const arr = read();
      const cleaned = arr.filter((n) => !String(n.id || '').startsWith('ntf_seed_'));
      if (cleaned.length !== arr.length) write(cleaned);
      localStorage.setItem(`${key}_cleaned_v2`, '1');
      localStorage.removeItem(key); // legacy "seeded" flag
    } catch { /* ignore */ }
  }
};
